import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import { AttachmentSource, type TransactionRecord } from '../transactions/transactionsTypes';
import type { LocalLinkChange } from './receiptsTypes';

type ReceiptRef = { id: string; documentId: string };

export const hasReceiptAttachment = (
  transaction: TransactionRecord,
  receipt: { id: string; documentId?: string },
): boolean => Boolean(transaction.attachments?.some(({ id }) => id === receipt.id || id === receipt.documentId));

/** A save sends the attachments it has, so one missing the linked receipt's attachment would drop it. */
export const ensureReceiptAttachment = <T extends TransactionRecord>(
  transaction: T,
  receiptId: string | undefined,
  documentId?: string,
): T => {
  if (!receiptId || hasReceiptAttachment(transaction, { id: receiptId, documentId })) {
    return transaction;
  }
  return transaction.set('attachments', [
    ...(transaction.attachments ?? []),
    { id: receiptId, source: AttachmentSource.Receipt },
  ]) as T;
};

/** Until the transaction syncs, a form can still carry detached or deleted receipts; a save would write them back. */
export const withoutReceiptAttachments = <T extends TransactionRecord>(
  transaction: T,
  receipts: ReadonlyArray<{ id: string; documentId?: string }>,
): T => {
  const removedIds = new Set(receipts.flatMap(({ id, documentId }) => (documentId ? [id, documentId] : [id])));
  const attachments = transaction.attachments ?? [];
  const keptAttachments = attachments.filter(({ id }) => !removedIds.has(id));
  return keptAttachments.length === attachments.length
    ? transaction
    : (transaction.set('attachments', keptAttachments) as T);
};

// A receipt link writes the attachment and the modification time; any other field means another edit.
const RECEIPT_LINK_FIELDS = ['attachments', 'modifiedAt'];

/** No Refresh needed: same content again, or only the shown receipt's attachment arriving (its sync can land first). */
export const isExpectedTransactionChange = (
  previousTxn: TransactionRecord,
  nextTxn: TransactionRecord,
  shownReceipt: ReceiptRef | undefined,
): boolean => {
  const shownIds = shownReceipt ? [shownReceipt.id, shownReceipt.documentId] : [];
  const previousAttachments = previousTxn.attachments ?? [];
  const nextAttachments = nextTxn.attachments ?? [];
  const previousIds = new Set(previousAttachments.map((attachment) => attachment.id));
  const addedAttachments = nextAttachments.filter((attachment) => !previousIds.has(attachment.id));

  return (
    addedAttachments.every((attachment) => shownIds.includes(attachment.id)) &&
    isEqual(
      nextAttachments.filter((attachment) => previousIds.has(attachment.id)),
      previousAttachments,
    ) &&
    isEqual(omit(previousTxn.toJS(), RECEIPT_LINK_FIELDS), omit(nextTxn.toJS(), RECEIPT_LINK_FIELDS))
  );
};

/**
 * True when another device changed the receipt link since `localChangesSince`. A receipt the transaction
 * already carried as an attachment is the editor's first lookup resolving.
 */
export const isOutsideReceiptLinkChange = ({
  transactionId,
  shownReceipt,
  shownAttachmentIds,
  liveReceipt,
  localLinkChanges,
  localChangesSince,
}: {
  transactionId: string | undefined;
  shownReceipt: ReceiptRef | undefined;
  /** Attachment ids of the transaction the shown receipt was taken from. */
  shownAttachmentIds: ReadonlySet<string>;
  liveReceipt: ReceiptRef | undefined;
  localLinkChanges: Readonly<Record<string, LocalLinkChange>>;
  localChangesSince: number;
}): boolean => {
  if (shownReceipt?.id === liveReceipt?.id) return false;
  const localChangeTo = (receipt: ReceiptRef): string | null | undefined => {
    const change = localLinkChanges[receipt.id];
    return change && change.version > localChangesSince ? change.transactionId : undefined;
  };
  const arrivalExplained =
    !liveReceipt ||
    localChangeTo(liveReceipt) === transactionId ||
    (!shownReceipt && (shownAttachmentIds.has(liveReceipt.id) || shownAttachmentIds.has(liveReceipt.documentId)));
  const removedHere = shownReceipt ? localChangeTo(shownReceipt) : undefined;
  const departureExplained = !shownReceipt || (removedHere !== undefined && removedHere !== transactionId);
  return !(arrivalExplained && departureExplained);
};
